// Section 22 person card: history, memory lines, family links, skill bars, and "why is this
// person unhappy" from explain(happinessTarget). Section 3: the player may rename anyone.
import {
  catalog,
  evaluateTarget,
  narrate,
  type Config,
  type Decision,
  type GameState,
  type MemoryKind,
  type Narration,
  type PersonId,
  type RationResource,
  type ReportEntry,
  type SkillId,
  type Target,
  type TraitId,
} from '../sim/api';
import { stampOfTurn, type SeasonStamp } from './calendar';
import { groupFactors, type FactorGroup, type FactorRow } from './explain';
import { peopleRows, type PersonFlag } from './people';

export type Relation = 'partner' | 'parent' | 'child' | 'household';

export interface FamilyLink {
  id: PersonId;
  name: string;
  relation: Relation;
  /** Present in the settlement (clickable); otherwise dead, departed or exiled. */
  present: boolean;
  fate: 'died' | 'left' | 'exiled' | null;
}

export interface MemoryLine extends SeasonStamp {
  kind: MemoryKind;
  resource: RationResource | null;
  amount: number | null;
  otherName: string | null;
}

export interface HistoryLine extends SeasonStamp {
  narration: Narration;
}

export interface PersonCardVM {
  id: PersonId;
  name: string;
  pendingName: string | null;
  sex: 'female' | 'male';
  ageYears: number;
  ageGroup: 'child' | 'adult' | 'elder';
  origin: 'founder' | 'born' | 'newcomer';
  jobLabel: string;
  pendingJob: boolean;
  health: number;
  strength: number;
  happiness: number;
  trust: number;
  traits: TraitId[];
  flags: PersonFlag[];
  householdName: string;
  householdSize: number;
  family: FamilyLink[];
  skills: { skill: SkillId; label: string; level: number; peak: number }[];
  memory: MemoryLine[];
  history: HistoryLine[];
  /** The happiness target the person is moving toward, with its factors (Section 16). */
  happinessTarget: number;
  happinessGroups: FactorGroup[];
  /** The negative terms, worst first: "why is this person unhappy". */
  unhappyBecause: FactorRow[];
  targets: { happiness: Target; strengthCap: Target; illnessRisk: Target };
}

function mentions(entry: ReportEntry, id: PersonId): boolean {
  const e = entry as Partial<Record<string, unknown>>;
  for (const key of ['personId', 'offenderId', 'victimId', 'patientId', 'healerId', 'motherId'])
    if (e[key] === id) return true;
  const ids = e.personIds;
  return Array.isArray(ids) && ids.includes(id);
}

const HISTORY_SKIP = new Set<ReportEntry['kind']>(['trust', 'production', 'shortage']);

export function personCardVM(
  game: GameState,
  draft: Decision | null,
  personId: PersonId,
  cfg?: Config,
): PersonCardVM | null {
  const p = game.people[personId];
  const row = peopleRows(game, draft).find((r) => r.id === personId);
  if (p === undefined || row === undefined) return null;
  const nameOf = (id: PersonId): string =>
    game.people[id]?.name ?? game.formerPeople[id]?.name ?? id;
  const link = (id: PersonId, relation: Relation): FamilyLink => {
    const former = game.formerPeople[id];
    return {
      id,
      name: nameOf(id),
      relation,
      present: game.people[id] !== undefined,
      fate: former === undefined ? null : former.fate.kind,
    };
  };
  const family: FamilyLink[] = [];
  if (p.partnerId !== null) family.push(link(p.partnerId, 'partner'));
  for (const id of p.parentIds) family.push(link(id, 'parent'));
  for (const id of p.childIds) family.push(link(id, 'child'));
  const household = game.households[p.householdId];
  for (const id of household?.memberIds ?? [])
    if (id !== p.id && !family.some((f) => f.id === id)) family.push(link(id, 'household'));

  const jobLabels = catalog().jobs;
  const skills = (Object.keys({ ...p.skillPeaks, ...p.skills }) as SkillId[])
    .map((skill) => ({
      skill,
      label: jobLabels.find((j) => j.id === skill)?.label ?? skill,
      level: p.skills[skill] ?? 0,
      peak: p.skillPeaks[skill] ?? p.skills[skill] ?? 0,
    }))
    .filter((s) => s.level > 0 || s.peak > 0)
    .sort((a, b) => b.level - a.level || (a.skill < b.skill ? -1 : 1));

  const memory = [...p.memory]
    .sort((a, b) => b.turn - a.turn)
    .map((m) => ({
      ...stampOfTurn(m.turn),
      kind: m.kind,
      resource: m.resource ?? null,
      amount: m.amount ?? null,
      otherName: m.otherId === undefined ? null : nameOf(m.otherId),
    }));

  const history: HistoryLine[] = [];
  for (const report of [...game.history].reverse()) {
    for (const entry of report.entries) {
      if (HISTORY_SKIP.has(entry.kind) || !mentions(entry, p.id)) continue;
      const narration = narrate(entry, game);
      if (narration !== null) history.push({ ...stampOfTurn(report.turn), narration });
    }
  }

  const happinessTarget: Target = { kind: 'happinessTarget', personId };
  const ev = evaluateTarget(game, happinessTarget, cfg);
  const groups = groupFactors(ev.factors);
  const unhappyBecause = groups
    .flatMap((g) => g.rows)
    .filter((r: FactorRow) => r.op !== 'base' && r.delta < 0)
    .sort((a, b) => a.delta - b.delta);

  return {
    id: p.id,
    name: p.name,
    pendingName: row.pendingName,
    sex: p.sex,
    ageYears: row.ageYears,
    ageGroup: row.ageGroup,
    origin: p.origin,
    jobLabel: row.jobLabel,
    pendingJob: row.pending,
    health: p.health,
    strength: p.strength,
    happiness: p.happiness,
    trust: p.trust,
    traits: p.traits,
    flags: row.flags,
    householdName: row.householdName,
    householdSize: household?.memberIds.length ?? 1,
    family,
    skills,
    memory,
    history,
    happinessTarget: ev.value,
    happinessGroups: groups,
    unhappyBecause,
    targets: {
      happiness: happinessTarget,
      strengthCap: { kind: 'strengthCap', personId },
      illnessRisk: { kind: 'illnessRisk', personId },
    },
  };
}
